import { notifyError, notifySuccess, serviceCall } from "@serenity-is/corelib";

/**
 * Drag-to-reorder for the dashboard's launch tiles.
 *
 * The order is `Cards.Rank`, which was already editable one row at a time on Settings › Cards.
 * This makes it direct: pick a tile up, drop it where it belongs, and the new ranks are saved.
 *
 * ⚠ It writes the SHARED rank, so a reorder changes the dashboard for everyone. That is consistent
 * with how the rest of the configurable surface behaves today (one dashboard, centrally arranged) —
 * but it is why the handles only appear for someone who may modify cards. If per-user ordering is
 * wanted, that is a different store (UserPreferenceService) and a different decision.
 *
 * ⚠ Native HTML5 drag and drop, no library. The tiles are anchors, which already have a browser
 * drag behaviour (dragging a link), so `draggable` has to be set explicitly on the element we want
 * dragged and the default link-drag suppressed via preventDefault on dragover.
 */
export function installCardReorder() {
    const container = document.querySelector<HTMLElement>(".m-modules, .m-quick");
    if (!container)
        return;

    // The server tells us whether this viewer may reorder. Without it, everyone would get handles
    // and only some would get a save — a control that works for one person and silently fails for
    // the next is worse than no control.
    if (container.dataset.reorderable !== "true")
        return;

    const items = Array.from(container.children) as HTMLElement[];
    if (items.length < 2)
        return;

    container.classList.add("m-reorderable");

    let dragged: HTMLElement | null = null;

    for (const item of items) {
        item.draggable = true;

        item.addEventListener("dragstart", e => {
            dragged = item;
            item.classList.add("m-drag-src");
            // Some browsers refuse to start a drag without data on the transfer.
            e.dataTransfer?.setData("text/plain", item.dataset.cardId ?? "");
            if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
        });

        item.addEventListener("dragend", () => {
            item.classList.remove("m-drag-src");
            container.querySelectorAll(".m-drag-over").forEach(x => x.classList.remove("m-drag-over"));
            dragged = null;
        });

        item.addEventListener("dragover", e => {
            if (!dragged || dragged === item)
                return;
            // ⚠ Without preventDefault the drop never fires AND the browser falls back to its own
            // link-drag, which navigates.
            e.preventDefault();
            item.classList.add("m-drag-over");
        });

        item.addEventListener("dragleave", () => item.classList.remove("m-drag-over"));

        item.addEventListener("drop", e => {
            e.preventDefault();
            item.classList.remove("m-drag-over");
            if (!dragged || dragged === item)
                return;

            // Insert before or after depending on which half was dropped on, so dragging rightwards
            // past a tile lands after it — the behaviour the gesture implies.
            const box = item.getBoundingClientRect();
            const after = (e.clientY - box.top) > box.height / 2 ||
                ((e.clientX - box.left) > box.width / 2 && Math.abs(e.clientY - box.top) < box.height);
            item.parentElement!.insertBefore(dragged, after ? item.nextSibling : item);

            persist(container);
        });

        // Keyboard equivalent: a drag-only control is unreachable without a mouse.
        item.addEventListener("keydown", e => {
            if (!e.altKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight"))
                return;
            e.preventDefault();
            const sibling = e.key === "ArrowLeft" ? item.previousElementSibling : item.nextElementSibling;
            if (!sibling)
                return;
            if (e.key === "ArrowLeft")
                item.parentElement!.insertBefore(item, sibling);
            else
                item.parentElement!.insertBefore(sibling, item);
            (item as HTMLElement).focus();
            persist(container);
        });
    }
}

/**
 * Saves the visible order as Card.Rank.
 *
 * ⚠ Ranks are rewritten in steps of 10, not 1..n. The seeded ranks came from the navigation's own
 * sort orders (1000, 2100, 8100 …) and a future import adds more; renumbering densely from 1 would
 * make every later insert collide. Steps leave room to drop something between two tiles.
 */
function persist(container: HTMLElement) {
    const ids = Array.from(container.children)
        .map(el => (el as HTMLElement).dataset.cardId)
        .filter(id => id) as string[];

    if (!ids.length)
        return;

    let done = 0;
    let failed = false;

    ids.forEach((id, index) => {
        serviceCall<any>({
            service: "Common/Card/Update",
            request: { EntityId: Number(id), Entity: { CardId: Number(id), Rank: (index + 1) * 10 } },
            blockUI: false,
            onSuccess: () => {
                if (++done === ids.length && !failed)
                    notifySuccess("Dashboard order saved.");
            },
            onError: () => {
                // One toast, not one per card: a permission or connection failure fails all of them
                // and twelve identical errors is not more informative than one.
                if (!failed) {
                    failed = true;
                    notifyError("The new order is shown but could not be saved.");
                }
                return true;
            }
        });
    });
}
